import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { REPORT_CARD_PRIVILEGE } from '@/lib/privilegeConstants';

const ReportCard = ({ title, description, icon: Icon, onClick }: {
  title: string;
  description: string;
  icon: any;
  onClick: () => void;
}) => (
  <Card 
    className="group cursor-pointer bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 min-h-[120px] sm:min-h-[140px] md:min-h-[160px] active:scale-95 active:shadow-lg"
    onClick={onClick}
  >
    <CardHeader className="flex flex-row items-start justify-between p-3 sm:p-4 md:p-6 pb-3 sm:pb-4 space-y-0">
      <div className="space-y-1 sm:space-y-2 flex-1 pr-2">
        <CardTitle className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 leading-tight">
          {title}
        </CardTitle>
        <p className="text-xs sm:text-sm md:text-base text-gray-500 leading-snug">
          {description}
        </p>
      </div>
      <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg bg-blue-500 flex items-center justify-center text-white flex-shrink-0">
        <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6">
          <Icon className="w-full h-full" />
        </div>
      </div>
    </CardHeader>
  </Card>
);

const ReportsTab = () => {
  const navigate = useNavigate();
  const { hasPrivilege, loading: privilegesLoading, privilegeIds } = useUserPrivileges();

  const allReportItems = [
    { title: "EOD", description: "View detailed statistical charts and analytics", icon: BarChart3, path: "/reports/statistics" },
    { title: "Invoice Report", description: "Invoice summary and details", icon: FileText, path: "/reports/invoice-report" },
    { title: "Customer Ledger", description: "Customer ledger report", icon: FileText, path: "/reports/customer-ledger" },
  ];

  const reportItems = privilegesLoading || privilegeIds.size === 0
    ? allReportItems
    : allReportItems.filter((item) => {
        const requiredPrevilageId = REPORT_CARD_PRIVILEGE[item.title];
        if (requiredPrevilageId == null) return true;
        return hasPrivilege(requiredPrevilageId);
      });

  const handleCardClick = (item: any) => {
    if (item.path) {
      navigate(item.path);
    } else {
      console.log(`Clicked on ${item.title}`);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 bg-white p-3 sm:p-4 md:p-6 rounded-lg min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        {/* <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
          Reports & Analytics
        </h3> */}
        <p className="hidden md:block text-sm sm:text-base text-gray-500">Access comprehensive business reports</p>
      </div>
      
      <div className="grid grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
        {reportItems.map((item, index) => (
          <ReportCard
            key={index}
            title={item.title}
            description={item.description}
            icon={item.icon}
            onClick={() => handleCardClick(item)}
          />
        ))}
      </div>
    </div>
  );
};

export default ReportsTab;
